import { loadStripe } from "@stripe/stripe-js";
/* -----------------Globals--------------- */
import { useEffect, useState } from "react";

/* -----------------Helpers & Hooks--------------- */
import { useAppStore } from "@hooks/useAppStore";
import { useLocalSearchParams } from "expo-router";

/* -----------------Child components--------------- */
import { Box, Loader } from "@memorang/ui";

/* -----------------Constants--------------- */
import { urls } from "@constants/urls";

/* -----------------Features--------------- */
import { handleCreateCheckoutSession } from "@features/dashboard";

import { pageViewTypes } from "@constants/tracking";
import { events } from "@constants/tracking";
import useAnalytics from "@hooks/useAnalytics";
import { useProductDetails } from "@hooks/useProduct";
import { isDev, isStaging, stagingBaseUrl } from "@memorang/constants";
/* -----------------Stripe--------------- */
import {
	EmbeddedCheckout,
	EmbeddedCheckoutProvider,
} from "@stripe/react-stripe-js";
import DocumentTitle from "components/DocumentTitle";

const stripeKey = isDev
	? process.env.EXPO_PUBLIC_STRIPE_KEY_TEST
	: process.env.EXPO_PUBLIC_STRIPE_KEY_LIVE;

if (!stripeKey) {
	throw new Error("Stripe key not found");
}
const stripePromise = loadStripe(stripeKey);

const CheckoutRoute = () => {
	const baseUrl = useAppStore((store) => store.app.links.baseUrl);
	const [clientSecret, setClientSecret] = useState<string | null>(null);

	useAnalytics({
		type: pageViewTypes.checkout,
		eventName: events.checkoutViewed,
	});

	const { data: productDetails } = useProductDetails();
	const { id, planId } = useLocalSearchParams<{
		id: string;
		planId?: string;
	}>();

	useEffect(() => {
		// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
		const fetchDetailsAndCreateSession = async () => {
			if (id) {
				const numericId = Number.parseInt(id);

				if (productDetails) {
					const productPlans = productDetails.plans;
					const selectedPlan = productPlans.find(
						(plan) => plan.id === Number(planId),
					);
					const routeSegment = `purchase/${numericId}/success?duration=${selectedPlan?.duration}&interval=${selectedPlan?.interval}&amount=${selectedPlan?.amount}&currency=${selectedPlan?.currency}&planId=${selectedPlan?.id}`;
					//TODO:Remove this once testing is done
					// const testDomain = 'https://psi-web-vivekneel-memorang.vercel.app';
					const redirectUrl = isDev
						? isStaging
							? `${stagingBaseUrl}/${routeSegment}`
							: `${urls.dev}/${routeSegment}`
						: `${baseUrl}/${routeSegment}`;

					const productDescription =
						"Unlock all items and maximize your test readiness";

					if (selectedPlan) {
						const res = await handleCreateCheckoutSession({
							selectedPlan,
							productDescription,
							productId: productDetails.id,
							redirectUrl,
							productName: productDetails.name,
						});
						setClientSecret(res);
					}
				}
			}
		};

		fetchDetailsAndCreateSession();
	}, [id, planId, baseUrl, productDetails]);

	if (!clientSecret) {
		return (
			<Box flex={1} justifyContent="center" alignItems="center" height="100%">
				<Loader />
			</Box>
		);
	}

	return (
		<>
			<DocumentTitle title="Checkout" />
			{clientSecret && (
				<EmbeddedCheckoutProvider
					stripe={stripePromise}
					options={{ clientSecret }}
				>
					<EmbeddedCheckout />
				</EmbeddedCheckoutProvider>
			)}
		</>
	);
};

export default CheckoutRoute;
